import { Button, Grid, TextField } from '@atgfr8/atg-ui-tailwind';
import { CurrencyUsdIcon } from '@atgfr8/icons';
import { Notification, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { closeModal } from '@store/modals';
import { orderStore, setOrderStore } from '@store/orders';
import { requestChecks } from '@store/orders/services';
import { LineItemViewModel } from '@typeDefinitions/lineItemTypes';
import { formatAmount, handleToast } from '@utils/utils';
import { fuelCharges } from '@views/order/constants';
import { min, size } from 'lodash';
import { onMount, Show } from 'solid-js';
import * as yup from 'yup';

type efsFormType = {
  requestAmount: number;
  comment: string;
  max: number;
};

type EFSModalProps = {
  firstLoadWarning: boolean;
  lineItem: LineItemViewModel;
};

export const EFSModal = (props: EFSModalProps) => {
  const filledReadonlyFieldClasses = [
    'bg-[rgba(0,0,0,0.12)]!',
    'rounded-t-[4px]!',
    'border-transparent!',
    'hover:border-transparent!',
    '[&>input]:pt-[16px]!',
    '[&>.floating-label]:top-[6px]!',
    '[&>.floating-label]:text-[14px]!',
    '[&>.floating-label]:text-[rgba(0,0,0,0.38)]!',
    '[&>.floating-label>.strip]:hidden!',
    '[&.has-value>.floating-label]:top-[8px]!',
    '[&.has-value>.floating-label]:text-[14px]!',
    '[&.has-value>.floating-label]:text-[rgba(0,0,0,0.38)]!',
    '[&.has-value>.floating-label]:font-normal!',
  ].join(' ');

  const { form, errors, data, setData, isSubmitting } = createForm<efsFormType>(
    {
      initialValues: {
        requestAmount: 0,
        comment: '',
        max: 0,
      },
      onSubmit: async (values) => {
        await handleEFSSubmit(values);
      },
      extend: validator({
        schema: yup.object().shape({
          requestAmount: yup.number().when('max', ([max]) => {
            return yup
              .number()
              .min(0.1, 'Request amount must be greater than 0')
              .max(Number(max), 'Request amount exceeded the maximum');
          }),
          comment: yup.string().nullable(),
        }),
      }),
    },
  );

  const handleEFSSubmit = async (values: efsFormType) => {
    const res = await requestChecks({
      amount: values.requestAmount,
      comment: values.comment,
      lineItemId: props.lineItem.id,
    });
    if (res && res.isSuccess) {
      if (Boolean(res.value) && size(res.value) > 0) {
        const { value } = res;
        const { index } = orderStore.activeTab;

        setOrderStore('order', 'loads', index, 'payments', value);
        setOrderStore('order', 'loads', index, 'lineItems', (lineItems) =>
          (lineItems ?? []).map((lineItem) => {
            if (lineItem.id === props.lineItem.id) {
              return {
                ...lineItem,
                status: 'Check Approved',
                referenceNumber: value[0].referenceNum,
              };
            }
            return lineItem;
          }),
        );
        handleToast(ToastType.Success, 'Request Sent and Approved');
      } else {
        setOrderStore(
          'order',
          'loads',
          orderStore.activeTab.index,
          'lineItems',
          (lineItems) =>
            (lineItems ?? []).map((lineItem) => {
              if (lineItem.id === props.lineItem.id) {
                return {
                  ...lineItem,
                  status: 'Requested',
                };
              }
              return lineItem;
            }),
        );
        handleToast(ToastType.Success, 'Request sent');
      }
      closeModal('EFSModal');
    }
  };

  const maxAmount = () => {
    const amount =
      Number(props.lineItem.rate) * Number(props.lineItem.quantity);
    return isFuelAdvance() ? min([amount * 0.4, 2000]) : amount;
  };

  const title = () => {
    return isFuelAdvance()
      ? 'Fuel Advance'
      : props.lineItem.type + ' - ' + props.lineItem.description;
  };

  const isFuelAdvance = () => fuelCharges.indexOf(props.lineItem.type!) != -1;

  onMount(() => {
    setData('requestAmount', isFuelAdvance() ? 0 : maxAmount()!);
    setData('max', maxAmount()!);
  });

  return (
    <form ref={form}>
      <Show when={isSubmitting()}>
        <AtgLoader />
      </Show>
      <div>
        <Grid container sx={{ 'min-height': '10vh' }} spacing={4}>
          <Grid item size={12}>
            <div class={'font-medium text-lg'}>Request Check</div>
          </Grid>
          <Show when={Boolean(props.firstLoadWarning)}>
            <Grid item size={12}>
              <Notification
                sxProps="mb-2!"
                type="warning"
                text="This is the first Armstrong load with this Carrier which is when
              fraud is most likely to occur. Take steps to protect yourself
              against EFS fraud. e.g. Ask for a picture of the side of the truck
              with their name and MC number to verify, or call the Carriers
              corporate office to verify your contact."
              ></Notification>
            </Grid>
          </Show>
          <Grid item size={6}>
            <TextField
              required
              label={`(${title()}) Enter New Amount`}
              type="number"
              onChange={(v: unknown) => {
                setData('requestAmount', Number(v as number));
              }}
              value={data().requestAmount}
              error={errors().requestAmount}
              startIcon={<CurrencyUsdIcon class="text-[rgba(0,0,0,0.54)]!" />}
              dataTestId="enter-new-amount-textInput"
            />
          </Grid>
          <Grid item size={3}>
            <TextField
              class={`${filledReadonlyFieldClasses} disabled-green-text-fill-color`}
              disabled
              label="Amount"
              value={formatAmount(data().requestAmount)}
              dataTestId="amount-textInput"
            />
          </Grid>
          <Grid item size={3}>
            <TextField
              class={`${filledReadonlyFieldClasses} disabled-red-text-fill-color`}
              disabled
              label="Max"
              value={formatAmount(data().max)}
              dataTestId="max-textInput"
            />
          </Grid>
          <Grid item size={12}>
            <TextField
              multiline
              rows={2}
              label="comment"
              onChange={(v: string) => {
                setData('comment', v);
              }}
              dataTestId="comment-textInput"
            />
          </Grid>
        </Grid>
        <div class="mt-4 flex justify-end gap-2">
          <Button
            class="rounded-xs!"
            type="submit"
            variant="contained"
            dataTestId="efs-request-button"
          >
            Request
          </Button>
          <Button
            class="rounded-xs!"
            dataTestId="efs-cancel-button"
            onClick={() => {
              closeModal('EFSModal');
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};
